// InvitePage.tsx
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "../styles/InvitePage.css";

// Helper hook to parse query parameters
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

interface InviteData {
  message?: string;
}

// Helper to get a cookie value
function getCookie(name: string) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

const InvitePage: React.FC = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const token = query.get("token");

  const [loading, setLoading] = useState<boolean>(true);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check for EmergencyExit cookie on mount and redirect if needed
  useEffect(() => {
    if (getCookie("EmergencyExit") === "true") {
      // Clear the cookie (set expiration in past)
      document.cookie = "EmergencyExit=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
      // Redirect to Google
      window.location.href = "https://www.google.co.uk";
    }
  }, []);

  useEffect(() => {
    console.log("Extracted token from URL:", token);

    // If no token is provided, stop loading and show login-only option
    if (!token) {
      setLoading(false);
      return;
    }

    // Validate the invitation token via API
    fetch(`https://api.psanalytics.co.uk/ncjp/validateInviteBasic.php?token=${token}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("❌ Invitation token is invalid or expired.");
        }
        return response.json();
      })
      .then((data: InviteData) => {
        setInviteData(data);
        setLoading(false);
      })
      .catch((err: Error) => {
        setError(err.message || "❌ An error occurred.");
        setLoading(false);
      });
  }, [token]);

  const handleLogin = () => {
    const redirectUrl = token
      ? "https://ncjp.psanalytics.co.uk/auth/realms/ncjp/protocol/openid-connect/auth" +
        "?client_id=ncjp-app" +
        "&redirect_uri=https://ncjp.psanalytics.co.uk/after-login" +
        "&response_type=code" +
        "&scope=openid email profile" +
        `&state=${encodeURIComponent(token)}`
      : "https://ncjp.psanalytics.co.uk/auth/realms/ncjp/protocol/openid-connect/auth" +
        "?client_id=ncjp-app" +
        "&redirect_uri=https://ncjp.psanalytics.co.uk/login-only-callback" +
        "&response_type=code" +
        "&scope=openid email profile" +
        "&kc_action=login"; // Forces login-only mode

    console.log("Redirecting to Login URL:", redirectUrl);
    window.location.href = redirectUrl;
  };

  const handleRegister = () => {
    if (token) {
      window.location.href =
        "https://ncjp.psanalytics.co.uk/auth/realms/ncjp/protocol/openid-connect/auth" +
        "?client_id=ncjp-app" +
        "&redirect_uri=https://ncjp.psanalytics.co.uk/after-login" +
        "&response_type=code" +
        "&kc_action=register" +
        "&scope=openid email profile" +
        `&state=${encodeURIComponent(token)}`;
    }
  };

  if (loading) {
    return <div className="govuk-body">🔄 Validating your invitation...</div>;
  }

  return (
    <div className="invite-container">
      <img
        src="/assets/crests/puk-logo.png"
        alt="PUK Logo"
        className="invite-logo"
      />

      <div className="invite-content">
        <div className="govuk-panel govuk-panel--confirmation">
          <h1 className="govuk-panel__title">National My Police Portal</h1>
          <div className="govuk-panel__body">
            {token ? (
              <>
                <p className="govuk-body">
                  You have recently made a service request which is suitable for online updates.
                  To receive those, please login or register for a National My Police Portal account.
                </p>
                {error && <p className="govuk-body error-text">{error}</p>}
              </>
            ) : (
              <p className="govuk-body">
                This portal is by invitation only. If you already have an account, please log in.
              </p>
            )}
          </div>
        </div>

        <div className="govuk-button-group">
          <button className="govuk-button" onClick={handleLogin}>
            ✅ I already have an account - Log in
          </button>
          {token && (
            <button className="govuk-button govuk-button--secondary" onClick={handleRegister}>
              🆕 I don’t have an account - Register
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvitePage;
